import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { isUuid } from '@leanin/shared';
import { asUserId } from '@leanin/shared/ids';
import { useMember } from '@/lib/use-session';
import {
  mapProfileViewToProfile,
  memberSinceFromCreatedAt,
} from '@/components/profile/mapper';
import {
  ProfileChrome,
  type ProfileHeaderData,
} from '@/components/profile/ProfileChrome';
import { ProfileChromeSkeleton } from '@/components/profile/ProfileChromeSkeleton';
import { ProfileContentView } from '@/components/profile/ProfileContentView';
import { ErrorState, useDelayedLoading } from '@leanin/ui';
import { RetryButton } from '@/components/states/index';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import { isNotFoundApiError } from '@/lib/api-error';
import {
  identityKeys,
  fetchMember,
  fetchMemberMutuals,
  fetchIsFollowing,
} from '@/lib/queries/identity';
import {
  fetchMyCircles,
  fetchMyNetworks,
} from '@/lib/queries/groups';
import { feedKeys, fetchPostsByAuthor } from '@/lib/queries/feed';
import { authoredPostToFeedPost } from '@/components/feed/feed-item-to-post';
import { formatRelativeShort } from '@/lib/format-date';
import type { Profile } from '@/components/profile/types';

export const Route = createFileRoute('/_authed/members/$id')({
  beforeLoad: ({ params }) => {
    if (!isUuid(params.id)) throw notFound();
  },
  component: MemberProfilePage,
});

function buildHeadline(data: {
  jobTitle?: string | null;
  company?: string | null;
  bio?: string | null;
}): string {
  const role = data.jobTitle ?? '';
  const company = data.company ?? '';
  if (role && company) return `${role} at ${company}`;
  return role || company || data.bio?.slice(0, 80) || '';
}

function MemberProfilePage() {
  const { id } = Route.useParams();
  const userId = asUserId(id);
  const viewer = useMember();

  const memberQuery = useQuery({
    queryKey: identityKeys.member(userId),
    queryFn: () => fetchMember(userId),
  });
  const followingQuery = useQuery({
    queryKey: identityKeys.follow(userId),
    queryFn: () => fetchIsFollowing(userId),
  });
  const mutualsQuery = useQuery({
    queryKey: identityKeys.memberMutuals(userId),
    queryFn: () => fetchMemberMutuals(userId),
  });
  // For the lists we use a member-specific endpoint; for simplicity reuse
  // existing fetchers when userId is the viewer (self profile lists).
  const circlesQuery = useQuery({
    queryKey: ['profile', 'circles', userId] as const,
    queryFn: () => fetchMyCircles({ pageSize: 6 }),
    enabled: viewer !== null && viewer.id === userId,
  });
  const networksQuery = useQuery({
    queryKey: ['profile', 'networks', userId] as const,
    queryFn: () => fetchMyNetworks(),
    enabled: viewer !== null && viewer.id === userId,
  });
  const postsQuery = useQuery({
    queryKey: feedKeys.authorPosts(userId, 5),
    queryFn: () => fetchPostsByAuthor(userId, 5),
  });

  const showSkeleton = useDelayedLoading(memberQuery.isLoading || viewer === null);
  if (showSkeleton) return <ProfileChromeSkeleton />;
  if (memberQuery.isLoading || viewer === null) return null;
  // 404 → branded NotFoundPage (member doesn't exist or is deactivated).
  // Other failures keep the retry surface. We render the page directly
  // (rather than `throw notFound()` from a render-context useQuery, which
  // TanStack Router routes to its catch-boundary, not its 404 surface).
  if (isNotFoundApiError(memberQuery.error)) return <NotFoundPage />;
  if (memberQuery.isError || !memberQuery.data) {
    return (
      <ErrorState
        title="Couldn't load profile"
        body="Something on our end didn't respond. Give it a moment and try again."
        primary={<RetryButton />}
      />
    );
  }

  const memberData = memberQuery.data;
  const isFollowing = followingQuery.data ?? false;
  const isViewer = viewer.id === memberData.id;

  const header: ProfileHeaderData = {
    id: memberData.id,
    name:
      [memberData.firstName, memberData.lastName]
        .filter(Boolean)
        .join(' ')
        .trim() || 'Member',
    headline: buildHeadline(memberData),
    location: memberData.location ?? '',
    memberSince: memberSinceFromCreatedAt(memberData.createdAt),
    photo: memberData.photoUrl ?? undefined,
    coverPhotoUrl: memberData.coverPhotoUrl ?? undefined,
    isViewer,
    isFollowing,
  };

  const collateralReady =
    !followingQuery.isLoading &&
    !mutualsQuery.isLoading &&
    !postsQuery.isLoading &&
    !(isViewer && (circlesQuery.isLoading || networksQuery.isLoading));

  let profile: Profile | null = null;
  if (collateralReady) {
    const mutuals = mutualsQuery.data ?? null;
    const posts = postsQuery.data ?? [];
    const circles = circlesQuery.data?.items ?? [];
    const networks = networksQuery.data?.items ?? [];

    profile = mapProfileViewToProfile(memberData, isFollowing, {
      circles: circles.map((c) => ({
        id: c.id,
        name: c.name,
        role: c.role === 'leader' ? 'Leader' : 'Member',
        meta: `${c.memberCount} member${c.memberCount === 1 ? '' : 's'}`,
        imageUrl: c.imageUrl,
      })),
      networks: networks.map((n) => ({
        id: n.id,
        name: n.name,
        memberCount: n.memberCount,
        imageUrl: n.imageUrl,
      })),
      posts: posts.map((p) => authoredPostToFeedPost(p, formatRelativeShort)),
      mutuals: (mutuals?.mutualMembers ?? []).slice(0, 6).map((m) => ({
        id: m.id,
        name: `${m.firstName} ${m.lastName}`.trim(),
        photo: m.photoUrl ?? '',
      })),
    });
  }

  return (
    <ProfileChrome header={header}>
      <ProfileContentView profile={profile} />
    </ProfileChrome>
  );
}
